import { SubscriptionStatus } from "fond/types";
import { SubscriptionActionSource, SubscriptionManageSource } from "fond/types/analytics";
import { PaymentFrequencies, PurchaseType } from "fond/types/stripe";
import { ChangeType } from "fond/views/Subscription/types";

import { event, number, oneOf, string } from "../schema-util";

import { noProps } from "./shared";

const manageSource = oneOf(SubscriptionManageSource).describe("Where the action was initiated from.");
const actionSource = oneOf(SubscriptionActionSource).describe("Where the action was initiated from.");

/**
 * Subscription related events.
 *
 * Events are prefixed with "subscription." followed by a location or action descriptor.
 */
export const subscription = {
  "subscription.home.inactive.resub.clicked": event({
    status: oneOf(Object.values(SubscriptionStatus)).describe("The status of the inactive subscription"),
  }).describe("User clicked Resubscribe button in the Inactive Subscription section of the homepage"),
  "subscription.manage-form.billing.period.selected": event({
    source: manageSource,
    period: oneOf(PaymentFrequencies).describe("The billing period selected by the user"),
  }).describe("User clicked billing period toggle"),
  "subscription.manage-form.tier.selected": event({
    source: manageSource,
    name: string().describe("The name of the subscription tier selected by the user, e.g. 'tier-1', 'tier-2', etc."),
  }).describe("User selected a subscription tier"),
  "subscription.manage-form.proceed.clicked": event({
    source: manageSource,
    licenses: number().optional().describe("The number of licenses the user is proceeding to purchase"),
    tier: string().optional().describe("The name of the subscription tier the user is proceeding to purchase, e.g. 'tier-1', 'tier-2', etc."),
    changeType: oneOf(Object.values(PurchaseType)).describe("The type of subscription change the user is proceeding with."),
  }).describe("User clicked the proceed (to checkout) button."),
  "subscription.premises.buy.clicked": event({
    source: actionSource,
  }).describe("User clicked Buy Premises button to add more premises to their subscription"),
  "subscription.package.upgrade.clicked": event({
    source: actionSource,
  }).describe(
    "User clicked Upgrade Package button to upgrade their subscription package. Note settings-upsell is used when the user does not yet have a planner package."
  ),
  "subscription.licenses.buy.more.clicked": event({
    source: actionSource,
  }).describe("User clicked on the 'Buy more' button in the homepage licenses insights section"),
  "subscription.billing-history.link.clicked": noProps.describe("User clicked the 'Billing history' link"),
  "subscription.change-type.submitted": event({
    changeType: oneOf(ChangeType).describe("The type of subscription change the user submitted."),
  }).describe("User submitted a subscription change form"),
  "subscription.checkout.promo-code.apply": noProps.describe("User applied a promo code during checkout"),
  "subscription.checkout.promo-code.remove": noProps.describe("User removed a promo code during checkout"),
  "subscription.checkout.completed": noProps.describe("User completed the subscription checkout process"),
};
